import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Statistic, Typography, List, Avatar, Tag, Button, Space, Empty, Table, Spin, Divider,
} from 'antd';
import {
  PhoneOutlined, ClockCircleOutlined, CheckCircleOutlined, WarningOutlined,
  RiseOutlined, EnvironmentOutlined, FileTextOutlined, PlusOutlined, EyeOutlined,
  TrophyOutlined, CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchCallDashboard } from '../services/callService';
import { TYPE_COLORS, PRIORITY_COLORS } from './callConstants';
import FollowupDetailDrawer from './FollowupDetailDrawer';

const { Title, Text } = Typography;

const CARDS = [
  { key: 'todayCalls', label: "Today's Calls", color: '#1890ff', icon: <PhoneOutlined /> },
  { key: 'todayPending', label: "Today's Pending", color: '#fa8c16', icon: <ClockCircleOutlined /> },
  { key: 'completedToday', label: 'Completed Today', color: '#52c41a', icon: <CheckCircleOutlined /> },
  { key: 'missed', label: 'Missed', color: '#f5222d', icon: <WarningOutlined /> },
  { key: 'overdue', label: 'Overdue', color: '#cf1322', icon: <WarningOutlined /> },
  { key: 'upcoming', label: 'Upcoming', color: '#722ed1', icon: <RiseOutlined /> },
  { key: 'siteVisitsToday', label: 'Site Visits Today', color: '#52c41a', icon: <EnvironmentOutlined /> },
  { key: 'officeVisits', label: 'Office Visits', color: '#13c2c2', icon: <EnvironmentOutlined /> },
  { key: 'materialSelection', label: 'Material Selection', color: '#722ed1', icon: <FileTextOutlined /> },
  { key: 'quotationsPending', label: 'Quotations Pending', color: '#fa8c16', icon: <FileTextOutlined /> },
  { key: 'converted', label: 'Converted', color: '#52c41a', icon: <RiseOutlined /> },
  { key: 'lost', label: 'Lost Leads', color: '#8c8c8c', icon: <WarningOutlined /> },
];

const CallDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchCallDashboard()); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counters = (data && data.counters) || {};

  // Each card deep-links to its filtered list/view.
  const today = dayjs().format('YYYY-MM-DD');
  const ft = (t) => `FollowupType=${encodeURIComponent(t)}`;
  const LINKS = {
    todayCalls: `/calls/history?date=${today}`,
    todayPending: '/calls/today',
    completedToday: `/calls/completed?date=${today}`,
    missed: '/calls/missed',
    overdue: '/calls/overdue',
    upcoming: '/calls/upcoming',
    siteVisitsToday: `/calls/today?${ft('Site Visit')}`,
    officeVisits: `/calls/upcoming?${ft('Office Visit')}`,
    materialSelection: `/calls/upcoming?${ft('Material Selection')}`,
    quotationsPending: `/calls/upcoming?${ft('Quotation Follow-up')}`,
    converted: '/leads?Priority=Converted',
    lost: '/leads?Priority=Lost',
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, padding: '4px 8px' }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Call &amp; Follow-up Dashboard</Title>
          <Text type="secondary">Your telecalling command center</Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<CalendarOutlined />} onClick={() => navigate('/calls/calendar')}>Calendar</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/calls/new')} style={{ borderRadius: 6 }}>
              New Follow-up
            </Button>
          </Space>
        </Col>
      </Row>

      {loading && !data ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            {CARDS.map(c => (
              <Col xs={12} sm={8} md={6} lg={4} key={c.key}>
                <Card bordered={false} style={{ borderRadius: 8, cursor: 'pointer' }} bodyStyle={{ padding: '14px 16px' }}
                  hoverable onClick={() => LINKS[c.key] && navigate(LINKS[c.key])}>
                  <Statistic
                    title={<span style={{ color: c.color }}>{c.icon} {c.label}</span>}
                    value={Number(counters[c.key]) || 0}
                    valueStyle={{ color: c.color, fontSize: 24, fontWeight: 600 }}
                  />
                </Card>
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={14}>
              <Card title={<span><ClockCircleOutlined /> Today's Follow-ups</span>} bordered={false}
                style={{ borderRadius: 8 }}
                extra={<Button type="link" onClick={() => navigate('/calls/today')}>View all</Button>}>
                {(data?.todayList || []).length === 0 ? (
                  <Empty description="No follow-ups scheduled for today" />
                ) : (
                  <List
                    dataSource={data.todayList}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button key="call" type="text" icon={<PhoneOutlined />} href={`tel:${item.CustomerMobile}`} />,
                          <Button key="view" type="text" icon={<EyeOutlined />} onClick={() => setDetail(item)} />,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Avatar style={{ backgroundColor: TYPE_COLORS[item.FollowupType] || '#1890ff' }}>
                            {item.ScheduledAt ? dayjs(item.ScheduledAt).format('HH') : '•'}</Avatar>}
                          title={
                            <Space>
                              <Text strong>{item.ScheduledAt ? dayjs(item.ScheduledAt).format('hh:mm A') : ''}</Text>
                              <Text>{item.CustomerName}</Text>
                              {item.Priority && <Tag color={PRIORITY_COLORS[item.Priority] || 'default'}>{item.Priority}</Tag>}
                            </Space>
                          }
                          description={
                            <Space size={6}>
                              {item.City && <span><EnvironmentOutlined /> {item.City}</span>}
                              {item.FollowupType && <Tag color={TYPE_COLORS[item.FollowupType] || 'blue'}>{item.FollowupType}</Tag>}
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Col>

            <Col xs={24} lg={10}>
              <Card title={<span><TrophyOutlined /> Executive Leaderboard</span>} bordered={false}
                style={{ borderRadius: 8, marginBottom: 16 }}>
                {(data?.leaderboard || []).length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" /> : (
                  <Table
                    size="small" pagination={false} rowKey="ExecutiveName"
                    dataSource={data.leaderboard}
                    columns={[
                      { title: 'Executive', dataIndex: 'ExecutiveName' },
                      { title: 'Calls', dataIndex: 'calls', align: 'center', width: 70 },
                      { title: 'Done', dataIndex: 'completed', align: 'center', width: 70 },
                      { title: 'Conv.', dataIndex: 'converted', align: 'center', width: 70 },
                    ]}
                  />
                )}
              </Card>
              <Card title={<span><PhoneOutlined /> Recent Calls</span>} bordered={false} style={{ borderRadius: 8 }}>
                {(data?.recent || []).length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recent calls" /> : (
                  <List
                    size="small"
                    dataSource={(data.recent || []).slice(0, 6)}
                    renderItem={(item) => (
                      <List.Item style={{ cursor: 'pointer' }} onClick={() => setDetail(item)}>
                        <List.Item.Meta
                          title={<Text>{item.CustomerName} {item.Outcome && <Tag color="blue">{item.Outcome}</Tag>}</Text>}
                          description={<Text type="secondary" style={{ fontSize: 12 }}>{item.CallAt ? dayjs(item.CallAt).format('DD MMM, hh:mm A') : ''} · {item.ExecutiveName || '—'}</Text>}
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </>
      )}

      <FollowupDetailDrawer open={!!detail} followup={detail} onClose={() => setDetail(null)} onChanged={load} />
    </div>
  );
};

export default CallDashboard;
